import { useCallback, useEffect, useState } from "react";
import {
  fetchUndelivered,
  resendOffer,
  type ResendOfferResult,
  type UndeliveredQuote,
} from "../../api/quoteDebug";
import styles from "../FlowSelector/FlowSelector.module.css";

// Template keys accepted by the worker's selectOfferTemplate().
const TEMPLATES = ["Sauna", "Hot Tub", "IJsbad"] as const;

const cell: React.CSSProperties = {
  padding: "10px 12px",
  borderBottom: "1px solid var(--border)",
  fontSize: 13,
  verticalAlign: "top",
  textAlign: "left",
};

const mono: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

function formatDate(ms: number): string {
  return new Date(ms).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
}

function formatTimings(t: Record<string, number | undefined>): string {
  return Object.entries(t)
    .filter(([, ms]) => ms !== undefined)
    .map(([stage, ms]) => `${stage} ${ms}ms`)
    .join(" · ");
}

function ResultPanel({ result }: { result: ResendOfferResult }) {
  if (result.ok) {
    const { hubspot } = result;
    return (
      <div style={{ ...mono, color: "var(--text-primary)" }}>
        ✅ Offer emailed to {result.emailedTo} —{" "}
        <a href={result.pdfUrl} target="_blank" rel="noreferrer">open PDF</a>{" "}
        ({result.pdfReused ? "existing PDF" : "new PDF"}, {Math.round(result.bytes / 1024)} KB)
        <div style={{ marginTop: 4 }}>
          HubSpot: email activity {hubspot.emailLogged ? "✅" : "❌"} · lead note {hubspot.noteAdded ? "✅" : "❌"}
          {hubspot.detail && <span style={{ color: "var(--text-muted)" }}> — {hubspot.detail}</span>}
        </div>
        <div style={{ color: "var(--text-muted)", marginTop: 4 }}>{formatTimings(result.timings)}</div>
      </div>
    );
  }
  return (
    <div style={{ ...mono, color: "var(--danger, #b42318)" }}>
      ❌ Failed at <strong>{result.stage}</strong>: {result.error}
      <div style={{ color: "var(--text-muted)", marginTop: 4 }}>{formatTimings(result.timings)}</div>
      {result.stack && (
        <details style={{ marginTop: 6, color: "var(--text-muted)" }}>
          <summary style={{ cursor: "pointer" }}>Stack trace</summary>
          {result.stack}
        </details>
      )}
    </div>
  );
}

export function DebugQuotes() {
  const [quotes, setQuotes] = useState<UndeliveredQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, ResendOfferResult>>({});
  const [templates, setTemplates] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setQuotes(await fetchUndelivered());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resend = async (q: UndeliveredQuote) => {
    const confirmed = window.confirm(
      `Email the offer ${q.quoteReference} to ${q.customerName} <${q.customerEmail}>?\n\n` +
        "The customer receives it immediately, and it is logged on their HubSpot contact and lead.",
    );
    if (!confirmed) return;
    setRunning((p) => ({ ...p, [q.id]: true }));
    try {
      const productType = q.productType ? undefined : (templates[q.id] ?? TEMPLATES[0]);
      const result = await resendOffer(q.id, productType);
      setResults((p) => ({ ...p, [q.id]: result }));
    } catch (err) {
      // The request itself failed (network/403), not the PDF pipeline.
      setResults((p) => ({
        ...p,
        [q.id]: { ok: false, stage: "load", error: `Request failed: ${err instanceof Error ? err.message : String(err)}`, stack: null, timings: {} },
      }));
    } finally {
      setRunning((p) => ({ ...p, [q.id]: false }));
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.inner} style={{ maxWidth: 1200 }}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Debug Quotes</h1>
            <p className={styles.subtitle}>
              Submitted quotes whose offer never reached the customer — no PDF, or a PDF that was never emailed.
              “Re-run &amp; email” creates the PDF if needed, emails it to the customer and logs it in HubSpot (email
              activity on the contact, note with the PDF link on the lead). Quotes already emailed are refused.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={load} disabled={loading}>Refresh</button>
            <button className="btn" onClick={() => { window.location.hash = ""; }}>← Back to flows</button>
          </div>
        </div>

        <div
          role="note"
          style={{
            marginBottom: 24,
            padding: "10px 14px",
            fontSize: 13,
            color: "var(--text-primary)",
            background: "var(--warm-brown-dim)",
            border: "1px solid rgba(140, 105, 55, 0.3)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          ℹ️ <strong>Last error</strong> is what the worker recorded during delivery. Offers are delivered from a
          queue and a failed render is retried up to 3 times, a minute apart — “retrying” means another attempt is
          still coming; “giving up” means all attempts failed. “incomplete” means the job hasn’t run yet (give it a
          few minutes) or was cut off before it could record anything. Empty means the quote predates error
          recording.
        </div>

        {loading && <div className={styles.skeleton} />}
        {!loading && error && <p className={styles.empty}>Error loading quotes: {error}</p>}
        {!loading && !error && quotes.length === 0 && <p className={styles.empty}>Every offer was delivered. 🎉</p>}

        {!loading && !error && quotes.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--bg-surface)" }}>
            <thead>
              <tr>
                {["Reference", "Submitted", "Customer", "Template", "Last error", ""].map((h) => (
                  <th key={h} style={{ ...cell, fontWeight: 600, color: "var(--text-muted)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => {
                const result = results[q.id];
                const done = result?.ok === true;
                return (
                  <tr key={q.id} style={done ? { opacity: 0.6 } : undefined}>
                    <td style={{ ...cell, ...mono }}>{q.quoteReference}</td>
                    <td style={cell}>{formatDate(q.createdAt)}</td>
                    <td style={cell}>
                      {q.customerName}
                      <div style={{ color: "var(--text-muted)" }}>{q.customerEmail}</div>
                    </td>
                    <td style={cell}>
                      {q.productType ?? (
                        <select
                          value={templates[q.id] ?? TEMPLATES[0]}
                          onChange={(e) => setTemplates((p) => ({ ...p, [q.id]: e.target.value }))}
                          title="Not stored for this quote — pick the template to render"
                        >
                          {TEMPLATES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ ...cell, maxWidth: 420 }}>
                      {q.pdfUrl && (
                        <div style={{ marginBottom: 4 }}>
                          PDF exists, not emailed —{" "}
                          <a href={q.pdfUrl} target="_blank" rel="noreferrer">open PDF</a>
                        </div>
                      )}
                      <div style={{ ...mono, color: "var(--text-muted)" }}>{q.pdfError ?? "—"}</div>
                      {result && <div style={{ marginTop: 8 }}><ResultPanel result={result} /></div>}
                    </td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>
                      <button
                        className="btn btn-sm btn-accent"
                        onClick={() => resend(q)}
                        disabled={running[q.id] || done}
                      >
                        {running[q.id] ? "Sending…" : done ? "Sent" : "Re-run & email"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
